import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AppBar, Avatar, Box, Button, CircularProgress, Collapse, CssBaseline, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TextField, Toolbar, Tooltip, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import { useLocation, useNavigate } from 'react-router-dom';
import navItems from '../../Utils/NavItems';
import { getUserContext } from './UserContext';
import { ExpandLess, ExpandMore, Logout } from '@mui/icons-material';
import { useBranchContext } from './BranchContext';
import { changeMyPassword } from '../services/attendanceService';
import { fetchMyAllowedScreens, getCachedAllowedScreens, isScreenAllowed } from '../../Utils/CommonUtils';
import AppSelect from '../UI/AppSelect';
import NotificationBell from './NotificationBell';

interface HeaderProps {
    onLogout: () => void;
    isDrawerExpanded: boolean;
    setIsDrawerExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    isMobileDrawerOpen: boolean;
    setIsMobileDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const drawerWidth = 260;
const collapsedDrawerWidth = 72;
const navList = navItems;

const topNavOrderByRole: Record<string, string[]> = {
    ADMIN: ['/', '/sales-performance', '/contracts', '/attendance/admin', '/enquiry', '/fee-collection', '/seminars', '/myschedule', '/certificate', '/course', '/my-courses', '/svcs'],
    'CENTER HEAD': ['/', '/sales-performance', '/contracts', '/attendance/admin', '/enquiry', '/fee-collection', '/seminars', '/myschedule', '/svcs', '/my-courses'],
    COUNSELLOR: ['/', '/sales-performance', '/attendance/admin', '/enquiry', '/fee-collection', '/seminars', '/myschedule', '/svcs'],
    FACULTY: ['/', '/attendance/trainee', '/myschedule', '/my-courses'],
    STUDENT: ['/', '/my-courses'],
    MAINTAINER: ['/', '/certificate', '/course', '/my-courses', '/svcs'],
    CERTIFICATE: ['/', '/certificate'],
};

const servicesOrderByRole: Record<string, string[]> = {
    ADMIN: ['/user-mgmt', '/notice', '/timetable', '/offer', '/editwebsite', '/ebooks'],
    'CENTER HEAD': ['/user-mgmt', '/notice', '/timetable'],
    COUNSELLOR: ['/timetable'],
    MAINTAINER: ['/notice', '/ebooks'],
};

const seminarsOrderByRole: Record<string, string[]> = {
    ADMIN: ['/seminars', '/seminars/add'],
    COUNSELLOR: ['/seminars', '/seminars/add'],
    'CENTER HEAD': ['/seminars'],
};

const Header: React.FC<HeaderProps> = ({ onLogout, isDrawerExpanded, setIsDrawerExpanded, isMobileDrawerOpen, setIsMobileDrawerOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const  user  = getUserContext();
    const [openMenu, setOpenMenu] = useState<{ [key: string]: boolean }>({});
    const [allowedScreens, setAllowedScreens] = useState<string[]>(getCachedAllowedScreens());
    const role = localStorage.getItem('role') || '*';
    const { branchList } = useBranchContext();
    const { selectedBranch, setSelectedBranch } = useBranchContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const headerHeight = isMobile ? 64 : 72;
    const isCompactNav = !isMobile && !isDrawerExpanded;
    const isActivePath = (path: string) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
    const displayName = String(user?.name || user?.username || '').trim();
    const displayGreetingName = displayName || 'there';
    const displayRole = String(user?.role || '').trim();
    const visibleBranches = useMemo(() => {
        if (user?.branch === 'ALL') {
            return branchList.filter((branch: any) => branch !== 'ALL');
        }
        return branchList.filter((branch: any) => branch === user?.branch);
    }, [branchList, user?.branch]);

        // Set the initial value for selectedBranch when the component mounts
        useEffect(() => {
            if (user?.branch === 'ALL') {
            // Prefer JAYANAGAR as the default branch for users who can access all branches.
            const nonAllBranches = (branchList || []).filter((branch) => branch !== 'ALL');
            const jayanagarBranch = nonAllBranches.find(
                (branch) => String(branch || '').trim().toUpperCase() === 'JAYANAGAR'
            );
            const defaultBranch = jayanagarBranch || nonAllBranches[0];
            setSelectedBranch(defaultBranch || ''); // Fallback to empty if no branch is found
            } else {
            // If user.branch is not "ALL", use user.branch as the selected branch
            setSelectedBranch(user?.branch || '');
            }
        }, [branchList, setSelectedBranch, user?.branch]);

        // Prevent main background page from scrolling when mobile sidebar is open
        useEffect(() => {
            if (isMobile && isMobileDrawerOpen) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
            return () => {
                document.body.style.overflow = '';
            };
        }, [isMobile, isMobileDrawerOpen]);

    // Toggle the menu state for a specific item
    const handleToggle = (path: string) => {
        setOpenMenu((prev: { [key: string]: boolean }) => ({ ...prev, [path]: !prev[path] }));
      };

    const handleChange = (e: { target: { value: string } }) => {
        setSelectedBranch(e.target.value);
    };

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl((prev) => (prev ? null : event.currentTarget));
    };
    const handleProfileClose = () => {
      setAnchorEl(null);
    };

    // Change-password dialog
    const [pwDialog, setPwDialog] = useState(false);
    const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
    const [pwSaving, setPwSaving] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSuccess, setPwSuccess] = useState('');

    const openPwDialog = () => { setPwDialog(true); setPwForm({ current: '', next: '', confirm: '' }); setPwError(''); setPwSuccess(''); handleProfileClose(); };

    const handleChangePassword = async () => {
      if (!pwForm.current || !pwForm.next || !pwForm.confirm) { setPwError('All fields are required.'); return; }
      if (pwForm.next !== pwForm.confirm) { setPwError('New passwords do not match.'); return; }
            if (pwForm.next.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
      setPwSaving(true); setPwError('');
      try {
                await changeMyPassword(pwForm.current, pwForm.next, pwForm.confirm);
                setPwSuccess('Password changed successfully.');
        setPwForm({ current: '', next: '', confirm: '' });
                setPwDialog(false);
      } catch (e: any) {
        setPwError(e.message || 'Failed to change password');
      } finally {
        setPwSaving(false);
      }
    };

        const openProfilePage = () => {
            handleProfileClose();
            navigate('/profile');
        };

        const handleLogoutClick = () => {
          handleProfileClose();
          onLogout();
        };

                const openProfileFromMobileDrawer = () => {
                    navigate('/profile');
                    setIsMobileDrawerOpen(false);
                };

                const logoutFromMobileDrawer = () => {
                    setIsMobileDrawerOpen(false);
                    onLogout();
                };

    const handleDrawerToggle = () => {
        if (isMobile) {
            setIsMobileDrawerOpen((prevState) => !prevState);
            return;
        }
        setIsDrawerExpanded((prevState) => !prevState);
    };

    const handleClick = (path: string, hasChildren: boolean) => {
        if(hasChildren){
            console.log(path)
        }
        else{
            navigate(path);
            if (isMobile) {
                setIsMobileDrawerOpen(false);
            }
        }
        
    };

    const canAccess = (roles: string[]) => roles.includes(role) || roles.includes('*');

    useEffect(() => {
        let cancelled = false;

        const loadScreens = async () => {
            const screens = await fetchMyAllowedScreens(role);
            if (!cancelled) {
                setAllowedScreens(screens);
            }
        };

        loadScreens();
        return () => {
            cancelled = true;
        };
    }, [role]);

    const canAccessPath = useMemo(() => {
        const hasDynamicRules = allowedScreens.length > 0;
        return (path: string, roles: string[]) => {
            if (hasDynamicRules) {
                return isScreenAllowed(path, allowedScreens);
            }
            return canAccess(roles);
        };
    }, [allowedScreens, canAccess]);

    const orderByPreferredPaths = useMemo(() => {
        const topOrder = topNavOrderByRole[role] || [];
        const servicesOrder = servicesOrderByRole[role] || [];
        const seminarsOrder = seminarsOrderByRole[role] || [];

        return (items: typeof navItems, sectionPath?: string) => {
            const currentOrder = sectionPath === '/svcs'
                ? servicesOrder
                : sectionPath === '/seminars'
                    ? seminarsOrder
                    : topOrder;

            const rank = new Map(currentOrder.map((path, index) => [path, index]));
            return [...items].sort((a, b) => {
                const aRank = rank.has(a.path) ? (rank.get(a.path) as number) : Number.MAX_SAFE_INTEGER;
                const bRank = rank.has(b.path) ? (rank.get(b.path) as number) : Number.MAX_SAFE_INTEGER;
                if (aRank !== bRank) return aRank - bRank;
                return a.title.localeCompare(b.title);
            });
        };
    }, [role]);

    const visibleNavItems = useMemo(() => {
        const topVisible = navList
            .filter((item) => canAccessPath(item.path, item.roles))
            .map((item) => {
                const hasChildren = Boolean(item.children && item.children.length > 0);
                if (!hasChildren) {
                    return item;
                }

                const visibleChildren = (item.children || []).filter((child) => canAccessPath(child.path, child.roles));
                return {
                    ...item,
                    children: orderByPreferredPaths(visibleChildren, item.path),
                };
            })
            .filter((item) => {
                if (!item.children) return true;
                return item.children.length > 0;
            });

        return orderByPreferredPaths(topVisible);
    }, [canAccessPath, orderByPreferredPaths]);

    const drawer = (
        <Box
            sx={{ 
                position: 'relative',
                backgroundColor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'divider',
                width: '100%',
                height: '100%',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'background.default',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(30, 60, 114, 0.28)',
                    borderRadius: '3px',
                    '&:hover': {
                        background: 'primary.main',
                    },
                },
            }}
        >
            <Box sx={{ display: { xs: 'block', sm: 'none' }, px: 1.5, pt: 1.5, pb: 1 }}>
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.25, py: 1, backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Avatar src={(user as any)?.photoUrl || ''} sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontWeight: 700 }}>
                                {user?.username?.[0]?.toUpperCase()}
                            </Avatar>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                                    Signed in as
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                                    {user?.username}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <ListItem disablePadding>
                        <ListItemButton onClick={openProfileFromMobileDrawer} sx={{ minHeight: 44 }}>
                            <ListItemIcon sx={{ minWidth: 34, color: '#475569' }}>
                                <AccountCircleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Profile" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton onClick={logoutFromMobileDrawer} sx={{ minHeight: 44 }}>
                            <ListItemIcon sx={{ minWidth: 34, color: '#dc2626' }}>
                                <Logout fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#dc2626' }} />
                        </ListItemButton>
                    </ListItem>
                </Box>
                <AppSelect
                    label="Branch"
                    name="branch"
                    value={selectedBranch || ''}
                    onChange={handleChange}
                    required
                    fullWidth
                    placeholder="Select branch"
                    options={visibleBranches}
                    sx={{
                        mt: 1.25,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#f9fafb',
                            '&:hover': {
                                backgroundColor: '#f3f4f6',
                            },
                        },
                    }}
                />
                <Divider sx={{ mt: 1.5 }} />
            </Box>
            <List sx={{ pt: 1.5, px: 1 }}>
                {visibleNavItems
                    .map((item) => (
                        <Box key={item.title}>
                            <Tooltip 
                                title={isCompactNav ? item.title : ''} 
                                placement="right" 
                                arrow
                            >     
                                <ListItem disablePadding sx={{ mb: 0.5 }}>
                                    {item.children && item.children.length > 0 ? (
                                        <ListItemButton
                                            sx={{
                                                borderRadius: 2,
                                                minHeight: 48,
                                                justifyContent: isCompactNav ? 'center' : 'initial',
                                                px: isCompactNav ? 2.5 : 2,
                                                transition: 'all 0.2s',
                                                backgroundColor: isActivePath(item.path) ? 'rgba(30, 60, 114, 0.1)' : 'transparent',
                                                '& .MuiListItemIcon-root': {
                                                    color: isActivePath(item.path) ? 'primary.main' : 'text.secondary',
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(30, 60, 114, 0.06)',
                                                    '& .MuiListItemIcon-root': {
                                                        color: 'primary.main',
                                                    },
                                                },
                                            }}
                                            onClick={item.section ? undefined : () => {
                                                if (!isCompactNav) {
                                                    handleToggle(item.path);
                                                } else {
                                                    setIsDrawerExpanded(true);
                                                    setTimeout(() => handleToggle(item.path), 100);
                                                }
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: isCompactNav ? 'auto' : 2,
                                                    justifyContent: 'center',
                                                    color: isActivePath(item.path) ? 'primary.main' : 'text.secondary',
                                                    transition: 'all 0.2s',
                                                }}
                                            >
                                                {item.icon && <item.icon />}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.title}
                                                sx={{
                                                    opacity: isCompactNav ? 0 : 1,
                                                    transition: 'opacity 0.2s',
                                                }}
                                                primaryTypographyProps={{ 
                                                    fontWeight: 500, 
                                                    fontSize: '0.9375rem',
                                                    color: isActivePath(item.path) ? 'primary.main' : 'text.primary',
                                                }}
                                            />
                                            {!isCompactNav && item.children && !item.section && (
                                                <Box sx={{ 
                                                    color: '#94a3b8',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                    {openMenu[item.path] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                </Box>
                                            )}
                                        </ListItemButton>
                                    ) : (
                                        <ListItemButton
                                            sx={{
                                                borderRadius: 2,
                                                minHeight: 48,
                                                justifyContent: isCompactNav ? 'center' : 'initial',
                                                px: isCompactNav ? 2.5 : 2,
                                                transition: 'all 0.2s',
                                                backgroundColor: isActivePath(item.path) ? 'rgba(30, 60, 114, 0.1)' : 'transparent',
                                                '& .MuiListItemIcon-root': {
                                                    color: isActivePath(item.path) ? 'primary.main' : 'text.secondary',
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(30, 60, 114, 0.06)',
                                                    '& .MuiListItemIcon-root': {
                                                        color: 'primary.main',
                                                    },
                                                },
                                            }}
                                            onClick={() => handleClick(item.path, false)}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: isCompactNav ? 'auto' : 2,
                                                    justifyContent: 'center',
                                                    color: isActivePath(item.path) ? 'primary.main' : 'text.secondary',
                                                    transition: 'all 0.2s',
                                                }}
                                            >
                                                {item.icon && <item.icon />}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.title}
                                                sx={{
                                                    opacity: isCompactNav ? 0 : 1,
                                                    transition: 'opacity 0.2s',
                                                }}
                                                primaryTypographyProps={{ 
                                                    fontWeight: 500, 
                                                    fontSize: '0.9375rem',
                                                    color: isActivePath(item.path) ? 'primary.main' : 'text.primary',
                                                }}
                                            />
                                        </ListItemButton>
                                    )}
                                </ListItem>
                            </Tooltip>

                            {/* Submenu (children) */}
                            {item.children && !isCompactNav && (
                                <Collapse in={item.section || openMenu[item.path]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.children
                                            .map((child) => (
                                                <ListItem key={child.title} disablePadding sx={{ mb: 0.5 }}>
                                                    <ListItemButton
                                                        sx={{
                                                            pl: 4,
                                                            pr: 2,
                                                            borderRadius: 2,
                                                            minHeight: 40,
                                                            transition: 'all 0.2s',
                                                            backgroundColor: isActivePath(child.path) ? 'rgba(30, 60, 114, 0.1)' : 'transparent',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(30, 60, 114, 0.06)',
                                                                '& .MuiListItemIcon-root': {
                                                                    color: 'primary.main',
                                                                },
                                                            },
                                                        }}
                                                        onClick={() => handleClick(child.path, false)}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 0,
                                                                mr: 2,
                                                                color: isActivePath(child.path) ? 'primary.main' : 'text.secondary',
                                                                transition: 'all 0.2s',
                                                            }}
                                                        >
                                                            {child.icon && <child.icon sx={{ fontSize: '1.125rem' }} />}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={child.title}
                                                            primaryTypographyProps={{ 
                                                                fontSize: '0.875rem', 
                                                                color: isActivePath(child.path) ? 'primary.main' : 'text.secondary',
                                                                fontWeight: 400,
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                    </List>
                                </Collapse>
                            )}
                        </Box>
                    ))}
            </List>
        </Box>
    );

    return (
        <>
            <CssBaseline />
            <AppBar
                component="nav"
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #1e3c72 0%, #152c54 100%)',
                    color: '#ffffff',
                    position: 'fixed', 
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1200,
                    borderRadius: 0,
                    borderBottom: '3px solid',
                    borderColor: 'secondary.main',
                    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.18)',
                }}
            >
                <Toolbar sx={{ minHeight: `${headerHeight}px`, px: { xs: 1, sm: 2 }, gap: { xs: 0.5, sm: 1 } }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{
                            display: 'block',
                            mr: { xs: 1, sm: 2 },
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', lineHeight: 1 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#ffffff',
                                    fontFamily: '"Orbitron", sans-serif',
                                    fontWeight: 800,
                                    fontSize: '2.1rem',
                                    letterSpacing: '0.04em',
                                    lineHeight: 1.1,
                                }}
                            >
                                NICT
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                Control Panel
                            </Typography>
                        </Box>
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', lineHeight: 1, minWidth: 0 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#ffffff',
                                    fontFamily: '"Orbitron", sans-serif',
                                    fontWeight: 800,
                                    fontSize: '1.6rem',
                                    letterSpacing: '0.04em',
                                    lineHeight: 1.1,
                                }}
                                noWrap
                            >
                                NICT
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.6rem' }} noWrap>
                                Control Panel
                            </Typography>
                        </Box>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={0}
                        alignItems="center"
                        sx={{
                            ml: 0,
                            gap: 0,
                            columnGap: 0,
                            rowGap: 0,
                        }}
                    >
                        <NotificationBell />
                        <AppSelect
                            label="Branch"
                            name="branch"
                            value={selectedBranch}
                            onChange={handleChange}
                            required
                            placeholder="Select branch"
                            options={visibleBranches}
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                width: { sm: 200, md: 200, lg: 200 },
                                minWidth: 200,
                                maxWidth: 200,
                                mr: 1.5,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#ffffff',
                                    '& fieldset': { borderColor: 'transparent' },
                                    '&:hover': { backgroundColor: '#ffffff' },
                                    '&:hover fieldset': { borderColor: 'transparent' },
                                },
                            }}
                        />

                    {user &&
                        <Tooltip title="Account settings">
                        <>
                        <IconButton
                            onClick={handleProfileClick}
                                size="small"
                            sx={{
                                    ml: 0,
                                    p: 0.5,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                            }}
                            aria-controls={open ? 'account-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                        > 
                            <Avatar
                                sx={{
                                    width: { xs: 38, sm: 44 },
                                    height: { xs: 38, sm: 44 },
                                    bgcolor: 'secondary.main',
                                    color: '#ffffff',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    flexShrink: 0,
                                }}
                            >
                                {user.username[0].toUpperCase()}
                            </Avatar>
                            <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left', pr: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.1, fontWeight: 600, display: 'block' }}>
                                Hello, {displayGreetingName}!
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#ffffff', lineHeight: 1.1, fontWeight: 700, display: 'block', textTransform: 'capitalize' }}>
                                {displayRole || 'User'}
                              </Typography>
                            </Box>
                        </IconButton>
                        
                        <Menu
                            anchorEl={anchorEl}
                            id="account-menu"
                            open={open}
                            onClose={handleProfileClose}
                            slotProps={{
                            paper: {
                                elevation: 0,
                                sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                '& .MuiAvatar-root': {
                                    minWidth: 'auto',
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                                '&::before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                                },
                            },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem onClick={openProfilePage}>
                              <Avatar sx={{ width: 24, height: 24 }} /> Profile
                            </MenuItem>
                            <MenuItem onClick={openPwDialog}>
                              <ListItemIcon><LockIcon fontSize="small" /></ListItemIcon>
                              Change Password
                            </MenuItem>
                            <Divider />
                                                        <MenuItem onClick={handleLogoutClick}>
                              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                              Logout
                            </MenuItem>
                        </Menu>
                        </>
                        </Tooltip>
                    }
                    </Stack>

            {/* Change Password Dialog */}
            <Dialog open={pwDialog} onClose={() => setPwDialog(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Change Password</DialogTitle>
              <DialogContent>
                {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
                {pwSuccess && <Alert severity="success" sx={{ mb: 2 }}>{pwSuccess}</Alert>}
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField label="Current Password" type="password" size="small" fullWidth value={pwForm.current} onChange={(e) => setPwForm((p) => ({ ...p, current: e.target.value }))} />
                                    <TextField label="New Password" type="password" size="small" fullWidth value={pwForm.next} onChange={(e) => setPwForm((p) => ({ ...p, next: e.target.value }))} helperText="Minimum 8 characters" />
                  <TextField label="Confirm New Password" type="password" size="small" fullWidth value={pwForm.confirm} onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))} />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPwDialog(false)}>Cancel</Button>
                <Button variant="contained" disabled={pwSaving} onClick={handleChangePassword}>
                  {pwSaving ? <CircularProgress size={16} /> : 'Change Password'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* <Button
                color="inherit"
                variant="contained"
                onClick={onLogout}
                style={{
                    backgroundColor: "maroon",
                    color: "white",
                    marginLeft: "15px"
            }}
            >
            <PowerSettingsNewIcon style={{padding:"1px"}}/>
            
            </Button> */}
                </Toolbar>
            </AppBar>
            <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                open={isMobile ? isMobileDrawerOpen : true}
                onClose={() => setIsMobileDrawerOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: isMobile ? drawerWidth : (isDrawerExpanded ? drawerWidth : collapsedDrawerWidth),
                        top: isMobile ? 0 : `${headerHeight}px`,
                        height: isMobile ? '100dvh' : `calc(100% - ${headerHeight}px)`,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        overscrollBehavior: 'contain',
                        WebkitOverflowScrolling: 'touch',
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Header;
